import {
  Add,
  Button,
  Chevron,
  EmptyState,
  Heading,
  NotStartedIcon,
  PageHeader,
  Tag
} from '@lmc/ui-kit'
import { useRouter } from 'hooks/useRouter'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Dropdown, MenuProps } from 'antd'
import { useEffect, useRef, useState } from 'react'
import useResident from 'hooks/useResident'
import { Spinner } from 'components/generic'
import { Link, useNavigate } from 'react-router-dom'
import { ImportantContactType, roleOptions } from '@lmc/ts-common'
import { ProfileViewSection } from '../ResidentProfileScreen/components/ProfileViewSection'
import { DescriptionsItemType } from 'antd/es/descriptions'
import {
  ImportantContactDtos,
  ResidentItem
} from '@lmc/ts-common/lib/dtos/Resident'
import { scrollToTop } from '../ResidentProfileScreen/utils/utils'
import FamilyView from './forms/components/FamilyView'
import { useDispatch } from 'react-redux'
import Actions from 'actions'
import { resendInvite } from 'operations/Users'
import dayjs from 'dayjs'
import MenuGrid from 'screens/Residents/components/MenuGrid/MenuGrid'
import { getAddressString } from '../ResidentProfileScreen/utils/getAddressString'
import useFeatures from 'hooks/useFeatures'
import { ProfileContent } from '../ResidentProfileScreen/styledComponents'
import { ContactStatus } from './forms/PersonalContactForm'
import { relationshipOptions } from './forms/utils/relationshipOptions'
import { isBrowser, isTablet } from 'react-device-detect'

const isDesktop = isBrowser || isTablet

export const ResidentImportantPeopleScreen = ({
  testId
}: { testId?: string } = {}) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [isSendingInvite, setIsSendingInvite] = useState(false)
  const features = useFeatures()

  const openFamilyPortalModal = (
    contact: ImportantContactDtos,
    index: number,
    profile: ResidentItem
  ) => {
    const modalID = 'inviteFamily'

    dispatch(
      Actions.openModal({
        modalId: modalID,
        data: {
          residentId: profile.id,
          residentName: {
            first: profile.name.first,
            last: profile.name.last
          },
          importantContact: contact,
          index
        }
      })
    )
  }

  const topRef = useRef<HTMLDivElement | null>(null)
  const {
    params: { id }
  } = useRouter()

  const { resident, isLoading } = useResident({ id })

  useEffect(() => {
    scrollToTop(topRef)
  }, [])

  if (isLoading) {
    return (
      <Spinner
        data-testid={
          testId
            ? `${testId}-important-people-loading`
            : 'important-people-loading'
        }
      />
    )
  }

  if (resident === undefined) {
    return (
      <EmptyState
        title={t(supConstants.NO_RESIDENT)}
        data-testid={
          testId
            ? `${testId}-important-people-no-resident`
            : 'important-people-no-resident'
        }
      />
    )
  }

  const { importantContacts } = resident

  const residentName = `${resident.name.first} ${resident.name.last}`

  const appendIndexToImportantContact = (
    importantContact: ImportantContactDtos,
    index: number
  ) => ({
    ...importantContact,
    index
  })
  const personalContacts =
    importantContacts?.map(appendIndexToImportantContact).filter(
      (contact) =>
        contact.type === ImportantContactType.personal ||
        // @ts-ignore
        contact.type === 'personal' ||
        contact.type === undefined
    ) || []
  const professionalContacts =
    importantContacts?.map(appendIndexToImportantContact).filter(
      (contact) =>
        contact.type === ImportantContactType.professional ||
        // @ts-ignore
        contact.type === 'professional'
    ) || []

  const hasGP = Boolean(
    resident.gpAddress ||
      resident.gpCode ||
      resident.gpEmail ||
      resident.gpName ||
      resident.gpSurgeryName ||
      resident.gpTelephone
  )
  const hasProfessionalContacts = professionalContacts.length > 0
  const hasPersonalContacts = personalContacts.length > 0

  const hasItems = hasGP || hasProfessionalContacts || hasPersonalContacts

  return (
    <div
      ref={topRef}
      data-testid={
        testId ? `${testId}-important-people-screen` : 'important-people-screen'
      }
    >
      <PageHeader
        sticky
        title={`${residentName}'s ${t(supConstants.IMPORTANT_PEOPLE)}`}
        // This is a reused code snippet for the back button that's inside a header that's already inside the codebase. Might be a good idea to refactor it at some point
        onBack={
          !isDesktop
            ? () => navigate(`/residents/${id}/profile`, { replace: true })
            : undefined
        }
        data-testid={
          testId
            ? `${testId}-important-people-header`
            : 'important-people-header'
        }
      >
        <Dropdown
          menu={{ items: getDropdownItems(t, hasGP) }}
          trigger={['click']}
          data-testid={
            testId
              ? `${testId}-important-people-dropdown`
              : 'important-people-dropdown'
          }
        >
          <Button
            iconAlign="right"
            variant="primary"
            data-testid={
              testId
                ? `${testId}-important-people-add-contact-btn`
                : 'important-people-add-contact-btn'
            }
          >
            {t(supConstants.ADD_CONTACT)}
            <Chevron direction="down" />
          </Button>
        </Dropdown>
      </PageHeader>
      <StyledWrapper
        data-testid={
          testId
            ? `${testId}-important-people-content`
            : 'important-people-content'
        }
      >
        {hasItems ? (
          <>
            {hasGP && (
              <StyledSectionsWrapper
                data-testid={
                  testId
                    ? `${testId}-important-people-gp-section`
                    : 'important-people-gp-section'
                }
              >
                <HeadingWithMargins
                  level={1}
                  data-testid={
                    testId
                      ? `${testId}-important-people-gp-heading`
                      : 'important-people-gp-heading'
                  }
                >
                  {t(supConstants.GP)}
                </HeadingWithMargins>
                <MenuGridStyled
                  data-testid={
                    testId
                      ? `${testId}-important-people-gp-grid`
                      : 'important-people-gp-grid'
                  }
                >
                  <ProfileViewSection
                    isEditMode
                    title={resident.gpName || ''}
                    items={getGPContactViewSectionItems(resident, t)}
                    link="edit-gp"
                    testId={testId}
                  />
                </MenuGridStyled>
              </StyledSectionsWrapper>
            )}
            {hasProfessionalContacts && (
              <StyledSectionsWrapper
                data-testid={
                  testId
                    ? `${testId}-important-people-professional-section`
                    : 'important-people-professional-section'
                }
              >
                <HeadingWithMargins
                  level={1}
                  data-testid={
                    testId
                      ? `${testId}-important-people-professional-heading`
                      : 'important-people-professional-heading'
                  }
                >
                  {t(supConstants[ImportantContactType.professional])}
                </HeadingWithMargins>
                <MenuGridStyled
                  data-testid={
                    testId
                      ? `${testId}-important-people-professional-grid`
                      : 'important-people-professional-grid'
                  }
                >
                  {professionalContacts?.map((contact) => (
                    <ProfileViewSection
                      isEditMode
                      key={contact.index}
                      link={`${contact.index}/edit-professional`}
                      title={`${contact.name?.first} ${contact.name?.last}`}
                      items={getProfessionalProfileViewSectionItems(contact, t)}
                      subtitle={
                        <div
                          data-testid={
                            testId
                              ? `${testId}-important-people-professional-tags-${contact.index}`
                              : `important-people-professional-tags-${contact.index}`
                          }
                        >
                          {contact.lpaTypes?.map((lpaType) => (
                            <Tag
                              color="purple"
                              data-testid={
                                testId
                                  ? `${testId}-important-people-lpa-tag-${lpaType}`
                                  : `important-people-lpa-tag-${lpaType}`
                              }
                            >
                              {t(supConstants[`LPA_${lpaType}`])}
                            </Tag>
                          ))}
                        </div>
                      }
                      testId={testId}
                    >
                      {features?.family?.enabled && contact.familyHasAccess && (
                        <FamilyViewStyled
                          contact={contact}
                          residentName={resident.name.first}
                          items={familyPortalViewSectionItems(contact, t)}
                          title={t(supConstants.FAMILY_PORTAL)}
                          hasAccess={Boolean(contact.familyHasAccess)}
                          hasBeenInvited={Boolean(contact.familyDateInvited)}
                          hasAcceptedInvitation={Boolean(
                            contact.familyDateActivated
                          )}
                          onCreateInvite={() =>
                            openFamilyPortalModal(
                              contact,
                              contact.index,
                              resident
                            )
                          }
                          isSendingInvite={isSendingInvite}
                          onResendInvite={() => {
                            setIsSendingInvite(true)
                            resendInvite(contact.familyAccountId, () =>
                              setIsSendingInvite(false)
                            )()
                          }}
                        />
                      )}
                    </ProfileViewSection>
                  ))}
                </MenuGridStyled>
              </StyledSectionsWrapper>
            )}
            {hasPersonalContacts && (
              <StyledSectionsWrapper>
                <HeadingWithMargins level={1}>
                  {t(supConstants[ImportantContactType.personal])}
                </HeadingWithMargins>
                <MenuGridStyled>
                  {personalContacts?.map((contact) => (
                    <ProfileViewSection
                      isEditMode
                      key={contact.index}
                      link={`${contact.index}/edit-personal`}
                      title={`${contact.name?.first} ${contact.name?.last}`}
                      items={getPersonalProfileViewSectionItems(contact, t)}
                      subtitle={
                        <div>
                          {contact.lpaTypes?.map((lpaType) => (
                            <Tag color="purple">
                              {t(supConstants[`LPA_${lpaType}`])}
                            </Tag>
                          ))}
                          {contact.nextOfKin && (
                            <Tag color="magenta">
                              {t(supConstants.NEXT_OF_KIN)}
                            </Tag>
                          )}
                          {contact.unpaidCarer && (
                            <Tag color="geekblue">
                              {t(supConstants.UNPAID_CARER)}
                            </Tag>
                          )}
                          {contact.proxyForSubjectOfCare && (
                            <Tag color="green">{t(supConstants.PROXY)}</Tag>
                          )}
                        </div>
                      }
                    >
                      {features?.family?.enabled && contact.familyHasAccess && (
                        <FamilyViewStyled
                          contact={contact}
                          residentName={resident.name.first}
                          items={familyPortalViewSectionItems(contact, t)}
                          title={t(supConstants.FAMILY_PORTAL)}
                          hasAccess={Boolean(contact.familyHasAccess)}
                          hasBeenInvited={Boolean(contact.familyDateInvited)}
                          hasAcceptedInvitation={Boolean(
                            contact.familyDateActivated
                          )}
                          onCreateInvite={() =>
                            openFamilyPortalModal(
                              contact,
                              contact.index,
                              resident
                            )
                          }
                          isSendingInvite={isSendingInvite}
                          onResendInvite={() => {
                            setIsSendingInvite(true)
                            resendInvite(contact.familyAccountId, () =>
                              setIsSendingInvite(false)
                            )()
                          }}
                        />
                      )}
                    </ProfileViewSection>
                  ))}
                </MenuGridStyled>
              </StyledSectionsWrapper>
            )}
          </>
        ) : (
          <EmptyState
            icon={<NotStartedIcon />}
            title={t(supConstants.READY_SET_GO_THE_JOURNEY_STARTS_HERE)}
            description={t(
              supConstants.PRESS_THAT_BLUE_BUTTON_AT_THE_TOP_RIGHT_OF_THE_PAGE
            )}
            data-testid={
              testId
                ? `${testId}-important-people-empty-state`
                : 'important-people-empty-state'
            }
          />
        )}
      </StyledWrapper>
    </div>
  )
}

const getDropdownItems = (
  t: (key: string) => string,
  hasGP: boolean
): MenuProps['items'] => {
  const items: { key: string | number; link: string; title: string }[] =
    Object.values(ImportantContactType).map((value) => ({
      key: value,
      title: t(supConstants[value]),
      link: `add-${value}`
    }))

  if (!hasGP) {
    items.push({
      key: 'gp',
      title: t(supConstants.GP),
      link: 'edit-gp'
    })
  }

  const renderedItems = items.map((item) => ({
    label: (
      <LinkStyled key={item.key} to={item.link}>
        <Add />
        {item.title}
      </LinkStyled>
    ),
    key: item.key
  }))

  return renderedItems
}

const LinkStyled = styled(Link)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

const createMailtoLink = (email: string | undefined) =>
  email && <Link to={`mailto:${email}`}>{email}</Link>

const getProfessionalProfileViewSectionItems = (
  contact: ImportantContactDtos,
  t: (key: string) => string
): DescriptionsItemType[] => {
  return [
    {
      key: 'role',
      label: t(supConstants.ROLE),
      children:
        roleOptions?.find((option) => option.value === contact.relationship)
          ?.label || contact.relationship
    },
    {
      key: 'organisation',
      label: t(supConstants.ORGANISATION),
      children: contact.organisation
    },
    {
      key: 'speciality',
      label: t(supConstants.SPECIALITY),
      children: contact.speciality
    },
    {
      key: 'email',
      label: t(supConstants.EMAIL),
      children: createMailtoLink(contact.email)
    },
    {
      key: 'address',
      label: t(supConstants.ADDRESS),
      children: getAddressString(contact.address)
    },
    {
      key: 'phone',
      label: t(supConstants.TELEPHONE1),
      children: contact.telephone
    },
    {
      key: 'phone2',
      label: t(supConstants.TELEPHONE2),
      children: contact.telephoneSecondary
    },
    {
      key: 'contactStatus',
      label: t(supConstants.CONTACT_STATUS),
      children: t(supConstants[contact.contactStatus as ContactStatus])
    },
    {
      key: 'observations',
      label: t(supConstants.OBSERVATION_NOTES),
      children: contact.observations
    },
    {
      key: 'contactableTimes',
      label: t(supConstants.CONTACTABLE_TIMES),
      children: contact.contactableTimes
    },
    {
      key: 'familyComment',
      label: t(supConstants.JUSTIFICATION_OF_DECISION),
      children: contact.familyComment
    }
  ].filter((item) => item.children)
}

const getPersonalProfileViewSectionItems = (
  contact: ImportantContactDtos,
  t: (key: string) => string
): DescriptionsItemType[] => {
  return [
    {
      key: 'relationship',
      label: t(supConstants.RELATIONSHIP),
      children:
        relationshipOptions.find(
          (option) => option.value === contact.relationship
        )?.label || contact.relationship
    },
    {
      key: 'email',
      label: t(supConstants.EMAIL),
      children: createMailtoLink(contact.email)
    },
    {
      key: 'address',
      label: t(supConstants.ADDRESS),
      children: getAddressString(contact.address)
    },
    {
      key: 'phone',
      label: t(supConstants.TELEPHONE1),
      children: contact.telephone
    },
    {
      key: 'phone2',
      label: t(supConstants.TELEPHONE2),
      children: contact.telephoneSecondary
    },
    {
      key: 'contactStatus',
      label: t(supConstants.CONTACT_STATUS),
      children: t(supConstants[contact.contactStatus as ContactStatus])
    },
    {
      key: 'observations',
      label: t(supConstants.OBSERVATION_NOTES),
      children: contact.observations
    },
    {
      key: 'contactableTimes',
      label: t(supConstants.CONTACTABLE_TIMES),
      children: contact.contactableTimes
    },
    {
      key: 'familyComment',
      label: t(supConstants.JUSTIFICATION_OF_DECISION),
      children: contact.familyComment
    }
  ].filter((item) => item.children)
}

const getGPContactViewSectionItems = (
  resident: ResidentItem,
  t: (key: string) => string
): DescriptionsItemType[] => {
  return [
    {
      key: '1',
      label: t(supConstants.SURGERY_PRACTICE_NAME),
      children: resident.gpSurgeryName
    },
    {
      key: '2',
      label: t(supConstants.PRACTICE_CODE),
      children: resident.gpCode
    },
    {
      key: '3',
      label: t(supConstants.EMAIL),
      children: resident.gpEmail
    },
    {
      key: '4',
      label: t(supConstants.ADDRESS),
      children: getAddressString(resident.gpAddress)
    },
    {
      key: '5',
      label: t(supConstants.MOBILE_NUMBER),
      children: resident.gpTelephone
    }
  ].filter((item) => item.children)
}

const familyPortalViewSectionItems = (
  contact: ImportantContactDtos,
  t: (key: string) => string
): DescriptionsItemType[] => {
  return [
    {
      key: '1',
      label: t(supConstants.ACTIVE_SINCE),
      children: contact.familyDateActivated
    },
    {
      key: '2',
      label: t(supConstants.EDITED_BY),
      children: contact.familyUpdatedByData?.name
    },
    {
      key: '3',
      label: t(supConstants.EDIT_DATE),
      children: dayjs(contact.familyCommentDate).format('DD/MM/YYYY HH:mm')
    },
    {
      key: '4',
      label: t(supConstants.WHY_THIS_DECISION_HAS_BEEN_MADE),
      children: contact.familyComment
    }
  ]
}

const FamilyViewStyled = styled(FamilyView)`
  margin-top: 24px;
`

const StyledWrapper = styled(ProfileContent)`
  gap: 32px;
`

const HeadingWithMargins = styled(Heading)`
  margin-left: 24px;
`

const MenuGridStyled = styled(MenuGrid)`
  margin-top: 24px;
`

const StyledSectionsWrapper = styled.div``
